/**
 * Dashboard landing route.
 */
export const DASHBOARD_PATH = '/';

/**
 * Login route. A fresh login lands here and is redirected to the dashboard
 * shortly after — but that redirect settles after this check runs, so login is
 * treated as a dashboard-bound entry.
 */
export const LOGIN_PATH = '/login';

/**
 * Shifts Overview with the "Action Needed" list open.
 */
export const ACTION_NEEDED_PATH = '/dashboard/jobs/pending';

/**
 * Entry paths whose destination is the dashboard: the dashboard itself
 * (app-open while logged in) and the login page (fresh login). Landing on any
 * other authenticated route is a deep link we must not hijack.
 */
const DASHBOARD_BOUND_ENTRY_PATHS: string[] = [DASHBOARD_PATH, LOGIN_PATH];

interface IShouldAutoNavigateParams {
  /** Whether the auto-navigate experiment flag is enabled for this user. */
  isFlagEnabled: boolean;
  /** Number of shifts that currently need the office's action. */
  actionRequiredCount: number;
  /** The path the user would otherwise land on. */
  currentPath: string;
}

/**
 * Decides whether to auto-navigate the office to the Action Needed list.
 *
 * Only fires when the experiment flag is on, the user has at least one
 * action-needed shift, and they entered from a dashboard-bound path (the
 * dashboard or the login page) — so we never hijack a deep link or a refresh on
 * another page. When the flag is off this returns false and the legacy landing
 * behavior is left untouched.
 */
export const shouldAutoNavigateToActionNeeded = ({
  isFlagEnabled,
  actionRequiredCount,
  currentPath,
}: IShouldAutoNavigateParams): boolean =>
  isFlagEnabled &&
  actionRequiredCount > 0 &&
  DASHBOARD_BOUND_ENTRY_PATHS.includes(currentPath);
